<template>
  <!-- Error Display -->
  <div v-if="errors.general" class="alert alert-danger mt-6 mb-0" role="alert">
    {{ errors.general }}
    <button
      @click="errors.general = ''"
      class="btn btn--icon-sm btn--primary ms-auto"
    >
      <i class="icon icon-close"></i>
    </button>
  </div>

  <div class="cart-checkout row py-5 py-lg-7">
    <div class="col-12 col-lg-5">
      <div class="d-flex align-items-center gap-2">
        <h1 class="h2 mb-0">Jouw gegevens</h1>
      </div>
    </div>
  </div>

  <div class="cart-checkout row mb-xl-10">
    <div class="col-12 col-xl-7">
      <form @submit.prevent="submitOrder">
        <div class="form-section" v-if="!checkoutPayload">
          <div class="text-center py-4">
            <p>Je winkelmand is leeg</p>
            <a :href="s7Urls.cart" class="btn btn--primary"
              >Terug naar winkelmandje</a
            >
          </div>
        </div>

        <div v-if="checkoutPayload">
          <div
            v-if="accountExistedMessage"
            class="alert alert-info py-4 mb-7"
            role="alert"
          >
            <div>
              <h5>E-mailadres is al gekoppeld aan een account</h5>
              <p class="mb-3">
                Je kunt gewoon verder met afrekenen. Je bestelling wordt
                gekoppeld aan je bestaande account. Of je kan
                <a :href="s7Urls.account" target="_blank"
                  >direct inloggen op je bestaande account</a
                >.
              </p>
              <button
                type="button"
                @click="handleExistingAccountProceed"
                class="btn btn--cta"
              >
                Doorgaan naar afrekenen
              </button>
            </div>
          </div>

          <div
            v-if="submitErrors.length > 0"
            class="alert alert-danger py-4 mb-7"
            role="alert"
          >
            <div>
              <h5>Daar ging iets mis!</h5>
              <ul class="mb-0">
                <li v-for="error in submitErrors" :key="error">{{ error }}</li>
              </ul>
            </div>
          </div>

          <!-- Contactgegevens -->
          <div class="row mb-0 gx-1 gx-md-3">
            <div class="col-12">
              <h5>Contactgegevens</h5>
            </div>
          </div>
          <div class="row gx-1 gx-md-3">
            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="email"
                >Voer je e-mailadres in<span>*</span></label
              >
              <input
                type="email"
                id="email"
                v-model="customer.email"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="info@sanderskerstpakket.nl"
              />
            </div>
            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="phone"
                >Telefoonnummer<span>*</span></label
              >
              <input
                type="tel"
                minlength="4"
                id="phone"
                v-model="customer.phone"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="0318 571 382"
              />
            </div>
          </div>

          <div
            v-if="
              checkoutPayload && shouldShowAccountCreation && !isAccountRequired
            "
          >
            <div class="row" v-if="!isUserLoggedIn">
              <div class="col-12">
                <div class="form-check">
                  <input
                    type="checkbox"
                    id="createAccount"
                    v-model="createAccount"
                    class="form-check-input"
                    aria-controls="account-creation-fields"
                  />
                  <label for="createAccount" class="form-check-label">
                    Maak een account aan
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="
            !isUserLoggedIn &&
            (createAccount || isAccountRequired) &&
            shouldShowPasswordFields
          "
        >
          <fieldset id="account-creation-fields" class="mt-2">
            <legend class="visually-hidden">Account aanmaken</legend>

            <div class="row gx-1 gx-md-3">
              <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
                <label class="form-label" for="accountPassword"
                  >Wachtwoord<span>*</span></label
                >
                <input
                  type="password"
                  id="accountPassword"
                  v-model="accountData.password"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="Kies een veilig wachtwoord"
                />
              </div>
              <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
                <label class="form-label" for="accountPasswordConfirm"
                  >Bevestig wachtwoord<span>*</span></label
                >
                <input
                  type="password"
                  id="accountPasswordConfirm"
                  v-model="accountData.passwordConfirm"
                  required
                  class="form-control"
                  :class="{
                    'check-valid': showFormErrors,
                    'is-invalid':
                      accountData.password &&
                      accountData.passwordConfirm &&
                      accountData.password !== accountData.passwordConfirm,
                  }"
                  placeholder="Herhaal je wachtwoord"
                />
                <div
                  v-if="
                    accountData.password &&
                    accountData.passwordConfirm &&
                    accountData.password !== accountData.passwordConfirm
                  "
                  class="invalid-feedback d-block password-feedback"
                >
                  Wachtwoorden komen niet overeen
                </div>
                <div
                  v-else-if="
                    accountData.password &&
                    accountData.passwordConfirm &&
                    accountData.password === accountData.passwordConfirm
                  "
                  class="valid-feedback d-block password-feedback"
                >
                  ✓ Wachtwoorden komen overeen
                </div>
              </div>
            </div>
          </fieldset>
        </div>

        <div v-if="checkoutPayload">
          <div class="row mt-5 mt-lg-7 mb-md-1 gx-1 gx-md-3">
            <div class="col-12">
              <h5>Factuurgegevens</h5>
            </div>
          </div>
          <div class="row mb-0 gx-1 gx-md-3">
            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="firstName"
                >Voornaam<span>*</span></label
              >
              <input
                type="text"
                id="firstName"
                v-model="customer.firstName"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="Voornaam"
              />
            </div>
            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="lastName"
                >Achternaam<span>*</span></label
              >
              <input
                type="text"
                id="lastName"
                v-model="customer.lastName"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="Achternaam"
              />
            </div>

            <div class="col-12 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="company"
                >Bedrijfsnaam <span>(optioneel)</span></label
              >
              <input
                type="text"
                id="company"
                v-model="customer.company"
                class="form-control"
                placeholder="Sanders Gifts B.V."
              />
            </div>

            <div class="col-12 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="country">Land<span>*</span></label>
              <select
                id="country"
                v-model="customer.country"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
              >
                <option value="NL">🇳🇱 Nederland</option>
                <option value="BE">🇧🇪 België</option>
              </select>
            </div>

            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="street"
                >Straatnaam<span>*</span></label
              >
              <input
                type="text"
                id="street"
                v-model="customer.street"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="Standaardruiter"
              />
            </div>

            <div class="col-5 col-sm-3 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="houseNumber"
                >Huisnummer<span>*</span></label
              >
              <input
                type="number"
                id="houseNumber"
                v-model="customer.houseNumber"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="2"
              />
            </div>
            <div class="col-3 col-sm-3 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="houseAddition"
                >Toev. <span class="d-none d-sm-inline">(opt.)</span></label
              >
              <input
                type="text"
                id="houseAddition"
                v-model="customer.houseAddition"
                class="form-control"
                placeholder="A"
              />
            </div>
            <div class="col-4 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="postcode"
                >Postcode<span>*</span></label
              >
              <input
                type="text"
                id="postcode"
                v-model="customer.postcode"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="3905 PW"
              />
            </div>

            <div class="col-12 col-sm-6 mb-05 mb-lg-1 position-relative">
              <label class="form-label" for="city"
                >Plaatsnaam<span>*</span></label
              >
              <input
                type="text"
                id="city"
                v-model="customer.city"
                required
                class="form-control"
                :class="{ 'check-valid': showFormErrors }"
                placeholder="Veenendaal"
              />
            </div>
          </div>
        </div>

        <div v-if="checkoutPayload">
          <div class="row mt-0">
            <div class="col-12">
              <div class="form-check">
                <input
                  type="checkbox"
                  id="useDifferentShippingAddress"
                  v-model="useDifferentShippingAddress"
                  class="form-check-input"
                  aria-controls="shipping-address-fields"
                />
                <label
                  for="useDifferentShippingAddress"
                  class="form-check-label"
                >
                  Verzendadres is anders dan factuuradres
                </label>
              </div>
            </div>
          </div>
        </div>

        <div v-if="checkoutPayload && useDifferentShippingAddress">
          <fieldset id="shipping-address-fields" class="mt-7">
            <legend class="visually-hidden">Verzendadres gegevens</legend>

            <div class="row mb-1">
              <div class="col-12">
                <h5>Verzendadres</h5>
              </div>
            </div>

            <div class="row gx-1 gx-md-3">
              <div class="col-12 col-sm-6 mb-05 mb-lg-1">
                <label class="form-label" for="shippingFirstName"
                  >Voornaam<span>*</span></label
                >
                <input
                  type="text"
                  id="shippingFirstName"
                  v-model="shippingAddress.firstName"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="Voornaam"
                />
              </div>
              <div class="col-12 col-sm-6 mb-05 mb-lg-1">
                <label class="form-label" for="shippingLastName"
                  >Achternaam<span>*</span></label
                >
                <input
                  type="text"
                  id="shippingLastName"
                  v-model="shippingAddress.lastName"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="Achternaam"
                />
              </div>

              <div class="col-12 mb-05 mb-lg-1">
                <label class="form-label" for="shippingCompany"
                  >Bedrijfsnaam <span>(optioneel)</span></label
                >
                <input
                  type="text"
                  id="shippingCompany"
                  v-model="shippingAddress.company"
                  class="form-control"
                  placeholder="Sanders Gifts B.V."
                />
              </div>

              <div class="col-12 col-sm-6 mb-05 mb-lg-1">
                <label class="form-label" for="shippingCountry"
                  >Land<span>*</span></label
                >
                <select
                  id="shippingCountry"
                  v-model="shippingAddress.country"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                >
                  <option value="NL">🇳🇱 Nederland</option>
                  <option value="BE">🇧🇪 België</option>
                </select>
              </div>

              <div class="col-12 col-sm-6 mb-05 mb-lg-1">
                <label class="form-label" for="shippingStreet"
                  >Straatnaam<span>*</span></label
                >
                <input
                  type="text"
                  id="shippingStreet"
                  v-model="shippingAddress.street"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="Standaardruiter"
                />
              </div>

              <div class="col-5 col-sm-3 mb-05 mb-lg-1 position-relative">
                <label class="form-label" for="shippingHouseNumber"
                  >Huisnummer<span>*</span></label
                >
                <input
                  type="number"
                  id="shippingHouseNumber"
                  v-model="shippingAddress.houseNumber"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="2"
                />
              </div>
              <div class="col-3 col-sm-3 mb-05 mb-lg-1 position-relative">
                <label class="form-label" for="shippingHouseAddition"
                  >Toev. <span class="d-none d-sm-inline">(opt.)</span></label
                >
                <input
                  type="text"
                  id="shippingHouseAddition"
                  v-model="shippingAddress.houseAddition"
                  class="form-control"
                  placeholder="A"
                />
              </div>
              <div class="col-4 col-sm-6 mb-05 mb-lg-1 position-relative">
                <label class="form-label" for="shippingPostcode"
                  >Postcode<span>*</span></label
                >
                <input
                  type="text"
                  id="shippingPostcode"
                  v-model="shippingAddress.postcode"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="3905 PW"
                />
              </div>

              <div class="col-12 mb-05 mb-lg-1">
                <label class="form-label" for="shippingCity"
                  >Plaatsnaam<span>*</span></label
                >
                <input
                  type="text"
                  id="shippingCity"
                  v-model="shippingAddress.city"
                  required
                  class="form-control"
                  :class="{ 'check-valid': showFormErrors }"
                  placeholder="Veenendaal"
                />
              </div>
            </div>
          </fieldset>
        </div>

        <div v-if="checkoutPayload">
          <div class="row mt-5 mt-lg-7 mb-md-1 gx-1 gx-md-3">
            <div class="col-12">
              <h5>Levering</h5>
            </div>
          </div>
          <div class="row mb-2">
            <div class="col-12">
              <label class="form-label" for="deliveryDate"
                >Gewenste leverdatum <span>(optioneel)</span></label
              >
              <input
                type="date"
                id="deliveryDate"
                v-model="customer.deliveryDate"
                class="form-control"
                :min="minDeliveryDate"
              />
            </div>
          </div>
        </div>

        <div v-if="checkoutPayload">
          <div class="row mt-5 mt-lg-7 mb-md-1 gx-1 gx-md-3">
            <div class="col-12">
              <h5>Bezorging</h5>
            </div>
          </div>

          <div
            v-if="
              filteredShippingMethods.length === 0 && shippingMethods.length > 0
            "
            class="alert alert-info mb-3"
          >
            <i class="icon icon-info me-2"></i>
            Geen verzendmethodes beschikbaar voor
            {{
              currentCountry === 'NL'
                ? 'Nederland'
                : currentCountry === 'BE'
                  ? 'België'
                  : currentCountry
            }}. Probeer een ander land te selecteren.
          </div>

          <div class="d-flex flex-column gap-2 pb-1">
            <label
              v-for="method in filteredShippingMethods"
              :key="method.id"
              class="card-selection card-selection--radio"
            >
              <!-- Heading -->
              <span class="method-heading">
                <input
                  type="radio"
                  :value="method"
                  v-model="currentShippingMethod"
                  name="shipping"
                  class="form-check-input"
                />
                <strong class="method-title">{{ method.label }}</strong>
                <span
                  class="method-cost"
                  :class="{ 'fw-medium text-secondary': method.cost === 0 }"
                  >{{ method.cost_formatted }}</span
                >
              </span>

              <!-- Info -->
              <span class="method-info">
                <!-- Per Address Breakdown -->
                <div
                  v-if="
                    method.method_id === 'per_address' &&
                    method.meta?.per_address_breakdown
                  "
                  class="per-address-breakdown"
                >
                  <template
                    v-for="(typeData, typeKey) in method.meta
                      .per_address_breakdown.types"
                    :key="typeKey"
                  >
                    <div
                      v-if="typeData && typeData.count > 0"
                      class="breakdown-line"
                    >
                      <span class="breakdown-label">{{ typeData.label }}:</span>
                      <span class="breakdown-value">
                        {{ typeData.count }} x
                        {{
                          formatPrice(
                            typeData.price_per_address_ex_vat,
                            priceFormat,
                          )
                        }}
                      </span>
                    </div>
                  </template>
                </div>
              </span>
            </label>
          </div>
        </div>

        <!-- Bestelnotities -->
        <div v-if="checkoutPayload">
          <div class="row mt-5 mt-lg-7 mb-md-1 gx-1 gx-md-3">
            <div class="col-12">
              <h5>
                Bestelnotities
                <span>(optioneel)</span>
              </h5>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col-12">
              <textarea
                v-model="customer.orderNotes"
                class="form-control"
                rows="3"
                placeholder="Notities over je bestelling, bijvoorbeeld speciale notities voor aflevering."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Betaling -->
        <div v-if="checkoutPayload">
          <div class="row mt-5 mt-lg-7 mb-md-1 gx-1 gx-md-3">
            <div class="col-12">
              <h5>Betaling</h5>
            </div>
          </div>

          <div class="d-flex flex-column gap-2">
            <label
              v-for="method in paymentMethods"
              :key="method.id"
              class="card-selection card-selection--radio cart-checkout--payment"
            >
              <!-- Heading -->
              <span class="method-heading">
                <input
                  type="radio"
                  :value="method"
                  v-model="currentPaymentMethod"
                  name="payment"
                  class="form-check-input"
                />
                <span
                  v-if="method.icon"
                  class="method-icon"
                  v-html="method.icon"
                ></span>
                <strong class="method-title">{{ method.label }}</strong>
              </span>

              <!-- Info -->
              <span class="method-info" v-if="method.description">
                <span class="method-description fs-sm">
                  {{ method.description }}
                </span>
              </span>
            </label>
          </div>
        </div>

        <!-- Bestel -->
        <div v-if="checkoutPayload">
          <div
            class="row mt-2 mt-md-4 mt-lg-2 mb-05 mb-lg-1 position-relative gx-1 gx-md-3"
          >
            <div class="col-12">
              <hr class="border-gray-300 mt-1 mb-3" />
              <p>
                Door het plaatsen van een bestelling bij Sanders Gifts gaat u
                akkoord met de
                <a
                  href="#"
                  target="_blank"
                  class="text-decoration-underline fw-medium"
                  >algemene voorwaarden.</a
                >
              </p>
            </div>
            <div class="col-12 d-none d-xl-flex">
              <button
                type="button"
                class="btn mt-2 w-100"
                :class="{ 'btn--cta': isFormValid, 'btn--gray': !isFormValid }"
                :disabled="isSubmitting || submittingOrderNow"
                @click="submitOrder"
              >
                <span v-if="!isSubmitting && !submittingOrderNow"
                  >Doorgaan naar afrekenen</span
                >
                <span v-else>Bestelling wordt verwerkt...</span>
                <i class="icon icon-checkmark"></i>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>

    <div class="col-12 col-xl-5">
      <div class="sticky-top">
        <div class="summary">
          <div
            class="d-flex justify-content-between align-items-center"
            v-if="checkoutPayload"
          >
            <span class="d-block h4 mb-0">Samenvatting</span>
            <button
              @click="goBackToCart"
              class="btn btn--transparent btn--sm p-0"
            >
              <i class="icon icon-edit text-primary me-0"></i>
            </button>
          </div>

          <div v-if="checkoutPayload" class="d-flex flex-column gap-1 my-2">
            <div
              v-for="(item, index) in cartItems"
              :key="index"
              class="summary__item d-flex align-items-center gap-1"
            >
              <div class="summary__item-image">
                <figure
                  v-if="getCartItemImage(item)"
                  class="rimg rimg--100-100 rimg--contain"
                >
                  <img
                    :src="getCartItemImage(item).src"
                    :alt="getCartItemImage(item).alt"
                  />
                </figure>
              </div>
              <div class="summary__item-info">
                <span class="d-inline fw-medium">{{ item.quantity }}x</span>
                <span class="fw-medium ps-05 text-black d-inline">
                  {{ decodeHtml(item.packageName) }}</span
                >
                <span
                  v-if="
                    item.packageType === 'small' || item.s7_is_mailbox_package
                  "
                  class="d-block text-primary fw-medium fs-sm"
                >
                  Brievenbus
                </span>
              </div>
              <span class="summary__item-price">{{
                formatPrice(
                  this.calculatePricePerUnit(item) * (item.quantity || 1),
                  priceFormat,
                )
              }}</span>
            </div>
          </div>

          <hr class="border-gray-300 my-2" />

          <div class="row flex-column gy-1 mt-1" v-if="checkoutPayload">
            <div class="col">
              <div
                class="row align-items-center justify-content-between text-black"
              >
                <div class="col-auto">
                  <span>Subtotaal ex. BTW</span>
                </div>
                <div class="col-auto text-end">
                  <span>{{ formatPrice(subtotalExVat, priceFormat) }}</span>
                </div>
              </div>
            </div>

            <div v-if="volumeDiscountPercent > 0" class="col">
              <div
                class="row align-items-center justify-content-between text-black"
              >
                <div class="col-auto">
                  <span>Staffelkorting ({{ volumeDiscountPercent }}%)</span>
                </div>
                <div class="col-auto text-end">
                  <span
                    >-{{ formatPrice(volumeDiscountAmount, priceFormat) }}</span
                  >
                </div>
              </div>
            </div>
            <div
              v-for="item in vatRatesArray"
              :key="`${item.rate}-${item.isShipping ? 'shipping' : 'product'}`"
              class="col"
            >
              <div
                class="row align-items-center justify-content-between text-black"
              >
                <div class="col-auto">
                  <span v-if="item.isShipping"> BTW Verzending (21%) </span>
                  <span v-else>
                    BTW-{{ getVatRateLabel(item.rate) }} ({{
                      (item.rate * 100).toFixed(0)
                    }}%)
                  </span>
                </div>
                <div class="col-auto text-end">
                  <span>{{ formatPrice(item.amount, priceFormat) }}</span>
                </div>
              </div>
            </div>
            <div class="col" v-if="currentShippingMethod">
              <div
                class="row align-items-center justify-content-between text-black"
              >
                <div class="col-auto">
                  <span>Verzending</span>
                </div>
                <div class="col-auto text-end">
                  <span
                    :class="{
                      'fw-medium text-secondary':
                        currentShippingMethod.cost === 0,
                    }"
                    >{{
                      currentShippingMethod.cost === 0
                        ? 'Gratis'
                        : formatPrice(currentShippingMethod.cost, priceFormat)
                    }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col">
              <div
                class="row align-items-center justify-content-between text-black fw-semi-bold"
              >
                <div class="col-auto">
                  <span>Totaal</span>
                </div>
                <div class="col-auto text-end">
                  <span>{{ formatPrice(totalWithShipping, priceFormat) }}</span>
                </div>
              </div>
            </div>
          </div>

          <input
            v-if="checkoutPayload"
            type="hidden"
            name="s7_logo_attachment_id"
            :value="logoAttachmentId"
          />

          <button
            v-if="checkoutPayload"
            type="button"
            class="btn w-100 mt-4"
            :class="{ 'btn--cta': isFormValid, 'btn--gray': !isFormValid }"
            :disabled="isSubmitting || submittingOrderNow || !isFormValid"
            @click="submitOrder"
          >
            <span v-if="!isSubmitting && !submittingOrderNow"
              >Doorgaan naar afrekenen</span
            >
            <span v-else>Bestelling wordt verwerkt...</span>
            <i class="icon icon-checkmark"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  calculateVatBreakdownByProduct,
  formatPrice,
  getPriceFormat,
  getVolumeDiscountPercent,
  getHardcodedVolumeDiscount,
  roundCurrency,
} from './configurator/utils/priceUtils.js';
import { decodeHtml } from './configurator/utils/utils.js';

export default {
  name: 'Checkout',
  data() {
    return {
      customer: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        street: '',
        city: '',
        houseNumber: '',
        houseAddition: '',
        postcode: '',
        state: '',
        country: 'NL',
        deliveryDate: '',
        orderNotes: '',
        deliveryMethod: '',
      },
      useDifferentShippingAddress: false,
      createAccount: false,
      isUserLoggedIn: window.__S7_USER__
        ? window.__S7_USER__.isLoggedIn
        : false,
      accountData: {
        password: '',
        passwordConfirm: '',
      },
      woocommerceSettings: {
        enableGuestCheckout: true,
        enableSignupFromCheckout: false,
        generatePassword: false,
      },
      shippingAddress: {
        firstName: '',
        lastName: '',
        phone: '',
        company: '',
        street: '',
        city: '',
        houseNumber: '',
        houseAddition: '',
        postcode: '',
        state: '',
        country: 'NL',
      },
      shippingMethods: [],
      paymentMethods: [],
      currentShippingMethod: null,
      currentPaymentMethod: null,
      selectedShippingMethod: null,
      submitErrors: [],
      accountExistedMessage: false,
      isSubmitting: false,
      showFormErrors: false,
      ajaxNonce: '',
      storedVolumeDiscountPercent: 0,
      confirmedExistingAccount: false,
      submittingOrderNow: false,
      logoAttachmentId: '',
      s7Urls: {
        cart: '/winkelmandje',
        checkout: '/afrekenen',
        thankyou: '/afrekenen/order-received',
        account: '/mijn-account',
      },
      shippingMethodNotification: '',
      lastPackageType: null,
      lastCountry: null,
      errors: {},
      beginCheckoutTriggered: false,
      shippingInfoTriggered: false,
      paymentInfoTriggered: false,
      vatBreakdownData: {
        subtotalExVat: 0,
        subtotalAfterDiscount: 0,
        volumeDiscountPercent: 0,
        volumeDiscountAmount: 0,
        vatRates: new Map(),
        total: 0,
        totalInclVat: 0,
        totalVat: 0,
      },
    };
  },
  computed: {
    checkoutPayload() {
      return this.getCheckoutPayload();
    },
    cartItems() {
      const items = this.getCartItems();

      const processedItems = items.map((item) => {
        if (!item.pricePerUnitExVat || item.pricePerUnitExVat <= 0) {
          item.pricePerUnitExVat = this.calculatePricePerUnit(item);
        }

        if (!item.lineTotalExVat || item.lineTotalExVat <= 0) {
          item.lineTotalExVat = item.pricePerUnitExVat * (item.quantity || 1);
        }

        return item;
      });

      return processedItems;
    },
    cartItemsWithPrices() {
      return this.cartItems.map((item) => {
        return {
          ...item,
          pricePerUnit: this.calculatePricePerUnit(item),
          lineTotalExVat:
            this.calculatePricePerUnit(item) * (item.quantity || 1),
        };
      });
    },
    totalPrice() {
      return this.cartItemsWithPrices.reduce((total, item) => {
        return total + (item.lineTotalExVat || 0);
      }, 0);
    },
    priceFormat() {
      return getPriceFormat();
    },
    subtotalExVat() {
      return this.vatBreakdown.subtotalExVat;
    },
    originalSubtotalExVat() {
      return (
        this.vatBreakdown.originalSubtotalExVat ||
        this.vatBreakdown.subtotalExVat
      );
    },
    subtotalAfterDiscount() {
      return this.vatBreakdown.subtotalAfterDiscount;
    },
    volumeDiscountPercent() {
      return this.vatBreakdown.volumeDiscountPercent;
    },
    volumeDiscountAmount() {
      return this.vatBreakdown.volumeDiscountAmount;
    },

    vatBreakdown() {
      return this.vatBreakdownData;
    },
    vatRatesArray() {
      const rates = [];
      if (this.vatBreakdown.vatRates) {
        this.vatBreakdown.vatRates.forEach((amount, rate) => {
          rates.push({ rate, amount });
        });
      }

      // Add shipping VAT to the rates array (only if taxable)
      if (this.vatBreakdown.shippingVat > 0 && this.isShippingTaxable) {
        rates.push({
          rate: 0.21,
          amount: this.vatBreakdown.shippingVat,
          isShipping: true,
        });
      }
      return rates;
    },
    totalPrintingCosts() {
      return this.cartItems.reduce((total, item) => {
        if (item.personalization?.printing?.total_ex_vat) {
          return total + item.personalization.printing.total_ex_vat;
        }
        return total;
      }, 0);
    },
    totalWithShipping() {
      return this.vatBreakdown.totalInclVat;
    },
    isShippingTaxable() {
      if (!this.currentShippingMethod) {
        return true;
      }

      // Check tax_status from method (all WC methods)
      if (this.currentShippingMethod.tax_status) {
        return this.currentShippingMethod.tax_status !== 'none';
      }

      // Fallback for per_address (backward compatibility)
      if (this.currentShippingMethod.method_id === 'per_address') {
        return (
          this.currentShippingMethod.meta?.per_address_breakdown?.tax_status !==
          'none'
        );
      }

      return true;
    },
    minDeliveryDate() {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    },
    isFormValid() {
      const hasValidCustomer =
        this.customer.firstName &&
        this.customer.lastName &&
        this.customer.email &&
        this.customer.phone &&
        this.customer.country &&
        this.customer.street &&
        this.customer.city &&
        this.customer.houseNumber &&
        this.customer.postcode;

      const hasValidShippingAddress =
        !this.useDifferentShippingAddress ||
        (this.shippingAddress.firstName &&
          this.shippingAddress.lastName &&
          this.shippingAddress.country &&
          this.shippingAddress.street &&
          this.shippingAddress.city &&
          this.shippingAddress.houseNumber &&
          this.shippingAddress.postcode);

      const hasValidAccount = this.validateAccountFields();

      const hasValidMethods =
        this.currentShippingMethod &&
        this.currentPaymentMethod &&
        this.paymentMethods.length > 0 &&
        this.filteredShippingMethods.length > 0 &&
        this.filteredShippingMethods.some(
          (method) => method.id === this.currentShippingMethod.id,
        );

      const hasValidCart =
        this.cartItems.length > 0 &&
        this.cartItems.every(
          (item) =>
            item.packageName &&
            item.pricePerUnitExVat > 0 &&
            item.lineTotalExVat > 0 &&
            item.quantity > 0,
        );

      return (
        hasValidCustomer &&
        hasValidShippingAddress &&
        hasValidAccount &&
        hasValidMethods &&
        hasValidCart
      );
    },
    packageType() {
      if (!this.cartItems || this.cartItems.length === 0) {
        return null;
      }

      const hasBrievenbuspakket = this.cartItems.some(
        (item) => item.packageType === 'small',
      );

      if (hasBrievenbuspakket) {
        return 'brievenbuspakket';
      }

      const hasBrievenbusdoosje = this.cartItems.some(
        (item) => item.packagingMode === 'product' && item.packagingProduct,
      );

      if (hasBrievenbusdoosje) {
        return 'brievenbusdoosje';
      }

      return 'groot_pakket';
    },
    currentCountry() {
      return this.useDifferentShippingAddress
        ? this.shippingAddress.country
        : this.customer.country;
    },
    filteredShippingMethods() {
      if (!this.shippingMethods || this.shippingMethods.length === 0) {
        return [];
      }

      const currentCountry = this.currentCountry;

      return this.shippingMethods.filter((method) => {
        const zoneName = method.zone_name || '';
        const methodLabel = method.label || '';
        const methodId = method.method_id || '';

        if (methodId === 'pickup_location' || methodId === 'local_pickup') {
          return true;
        }

        const countryMappings = {
          NL: ['Nederland', 'Netherlands', 'NL'],
          BE: ['België', 'Belgium', 'BE'],
        };

        const countryKeywords = countryMappings[currentCountry] || [];

        return countryKeywords.some(
          (keyword) =>
            zoneName.toLowerCase().includes(keyword.toLowerCase()) ||
            methodLabel.toLowerCase().includes(keyword.toLowerCase()),
        );
      });
    },
    shouldShowAccountCreation() {
      return (
        this.woocommerceSettings.enableSignupFromCheckout &&
        !this.isUserLoggedIn
      );
    },
    shouldShowPasswordFields() {
      return (
        !this.isUserLoggedIn &&
        (this.createAccount || this.isAccountRequired) &&
        !this.woocommerceSettings.generatePassword
      );
    },
    isAccountRequired() {
      return (
        !this.isUserLoggedIn && !this.woocommerceSettings.enableGuestCheckout
      );
    },
  },
  watch: {
    isUserLoggedIn: {
      handler(newValue) {
        if (newValue) {
          // Reset account creation checkbox when user is logged in
          this.createAccount = false;
        }
      },
      immediate: true,
    },
    currentShippingMethod: {
      handler(newMethod, oldMethod) {
        if (newMethod && newMethod !== oldMethod) {
          this.selectedShippingMethod = newMethod;
        }

        if (newMethod) {
          this.updateVatBreakdown();
        }

        if (
          newMethod &&
          this.cartItems.length > 0 &&
          this.isAddressComplete()
        ) {
          this.$nextTick(() => {
            this.pushAddShippingInfoEvent();
          });
        }
      },
    },
    customer: {
      handler() {
        this.checkAndTriggerShippingInfo();
      },
      deep: true,
    },
    shippingAddress: {
      handler() {
        if (this.useDifferentShippingAddress) {
          this.checkAndTriggerShippingInfo();
        }
      },
      deep: true,
    },
    useDifferentShippingAddress: {
      handler() {
        this.checkAndTriggerShippingInfo();
      },
    },
    currentPaymentMethod: {
      handler(newMethod, oldMethod) {},
    },
    useDifferentShippingAddress: {
      handler(newValue) {
        if (!newValue) {
          this.shippingAddress = {
            firstName: '',
            lastName: '',
            phone: '',
            company: '',
            street: '',
            city: '',
            houseNumber: '',
            houseAddition: '',
            postcode: '',
            state: '',
            country: 'NL',
          };
        }
        this.saveCustomerData();

        if (
          this.currentShippingMethod &&
          this.cartItems.length > 0 &&
          this.isAddressComplete()
        ) {
          this.$nextTick(() => {
            if (!this.shippingInfoTriggered) {
              this.pushAddShippingInfoEvent();
              this.shippingInfoTriggered = true;
            }
          });
        }
      },
    },
    packageType: {
      handler(newType, oldType) {
        if (newType !== oldType && this.filteredShippingMethods.length > 0) {
          this.autoSelectShippingMethod();
        }
      },
    },
    currentCountry: {
      handler(newCountry, oldCountry) {
        if (newCountry !== oldCountry) {
          this.loadShippingMethods();
        }
      },
    },
    filteredShippingMethods: {
      handler(newMethods, oldMethods) {
        if (newMethods.length > 0 && newMethods !== oldMethods) {
          this.autoSelectShippingMethod();
        }
      },
    },
    cartItemsWithPrices: {
      handler() {
        this.updateVatBreakdown();
      },
      deep: true,
      immediate: false,
    },
    cartItems: {
      handler(newItems, oldItems) {
        if (newItems.length > 0 && !this.beginCheckoutTriggered) {
          this.$nextTick(() => {
            if (!this.beginCheckoutTriggered) {
              this.pushBeginCheckoutEvent();
              this.beginCheckoutTriggered = true;
            }
          });
        }
      },
      immediate: true,
    },
    storedVolumeDiscountPercent() {
      this.updateVatBreakdown();
    },
  },
  created() {
    // Checkout component initialized
  },
  async mounted() {
    this.loadUserStatus();
    this.loadCustomerData(); // Load localStorage first
    await this.loadAjaxNonce();
    await this.loadWooCommerceSettings();

    // Load user data after localStorage (fills empty fields only)
    // This ensures localStorage has priority while user data fills gaps
    if (this.isUserLoggedIn) {
      await this.loadLoggedInUserData();
    }

    // Load shipping/payment methods in parallel for better performance
    await Promise.all([this.loadShippingMethods(), this.loadPaymentMethods()]);

    this.loadS7Urls();

    // Retry user status loading in case data wasn't available initially
    setTimeout(() => {
      if (!this.isUserLoggedIn && window.__S7_USER__) {
        this.loadUserStatus();
      }
    }, 100);

    if (this.cartItems.length > 0) {
      this.updateVolumeDiscount().catch(() => {});
      this.updateVatBreakdown().catch(() => {});

      this.$nextTick(() => {
        if (!this.beginCheckoutTriggered) {
          this.pushBeginCheckoutEvent();
          this.beginCheckoutTriggered = true;
        }
      });
    }
  },
  methods: {
    decodeHtml,
    getHardcodedVolumeDiscount,

    getCartItemImage(cartItem) {
      if (cartItem?.isBasePackage && cartItem?.basePackage?.image) {
        return {
          src: cartItem.basePackage.image,
          alt: cartItem.basePackage.title || cartItem?.packageName || 'Pakket',
        };
      }

      const pkgImg =
        cartItem?.packaging?.image ||
        cartItem?.package?.image ||
        cartItem?.packagingProduct?.image;

      if (pkgImg) {
        return { src: pkgImg, alt: cartItem?.packageName || 'Pakket' };
      }

      if (cartItem?.basePackage?.image) {
        return {
          src: cartItem.basePackage.image,
          alt: cartItem.basePackage.title || cartItem?.packageName || 'Pakket',
        };
      }
      const firstProductImg =
        cartItem?.products?.[0]?.image || cartItem?.foodDrinks?.[0]?.image;

      if (firstProductImg) {
        const name =
          cartItem?.products?.[0]?.name ||
          cartItem?.foodDrinks?.[0]?.name ||
          'Product';
        return { src: firstProductImg, alt: name };
      }

      return null;
    },
    formatPrice,
    getVatRateLabel(rate) {
      if (Math.abs(rate - 0.09) < 0.001) return 'Laag';
      if (Math.abs(rate - 0) < 0.001) return 'Vrij';
      return 'Hoog';
    },
    goBackToCart() {
      window.location.href = this.s7Urls.cart;
    },
    validateAccountFields() {
      if (this.isAccountRequired || this.createAccount) {
        if (this.woocommerceSettings.generatePassword) {
          return true;
        }

        return (
          this.accountData.password &&
          this.accountData.passwordConfirm &&
          this.accountData.password === this.accountData.passwordConfirm
        );
      }
      return true;
    },
    async loadWooCommerceSettings() {
      try {
        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_get_woocommerce_settings',
            nonce: window.s7_nonces?.order || this.ajaxNonce,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.woocommerceSettings = data.data;

          if (this.isAccountRequired) {
            this.createAccount = true;
          }
        } else {
          throw new Error(
            data.data?.message || 'Failed to load WooCommerce settings',
          );
        }
      } catch (error) {
        this.showError('Kon WooCommerce instellingen niet laden');
        this.submitErrors.push('Kon WooCommerce instellingen niet laden.');
      }
    },
    getCheckoutPayload() {
      const payload = sessionStorage.getItem('s7_checkout_payload_v1');
      if (!payload) {
        return null;
      }
      try {
        return JSON.parse(payload);
      } catch (error) {
        return null;
      }
    },
    getCartItems() {
      // Use the same data source as Cart component
      try {
        const stored = localStorage.getItem('composedPackages');
        if (stored) {
          const items = JSON.parse(stored);

          items.forEach((item, index) => {
            if (
              !item.packaging ||
              (typeof item.packaging === 'object' &&
                Object.keys(item.packaging).length === 0)
            ) {
              item.packaging = {
                id: 'default-packaging',
                name: 'Standaard verpakking',
                price: 0,
                taxClass: 'standard',
                taxRate: 21,
                taxLabel: 'BTW',
                count: 1,
                quantity: 1,
              };
            }
          });

          return items || [];
        }
      } catch (error) {
        // Silent fail
      }
      return [];
    },

    loadS7Urls() {
      if (window.__S7_URLS__) {
        this.s7Urls = { ...this.s7Urls, ...window.__S7_URLS__ };
      }
    },
    loadUserStatus() {
      if (window.__S7_USER__) {
        this.isUserLoggedIn = window.__S7_USER__.isLoggedIn;
      } else {
        // Fallback: detect login status from WordPress body class or admin bar
        this.isUserLoggedIn =
          document.body.classList.contains('logged-in') ||
          document.querySelector('#wpadminbar') !== null;
      }
    },
    /**
     * Merge source data into target, only filling empty fields
     * @param {Object} target - Target object to merge into
     * @param {Object} source - Source object to merge from
     */
    mergeEmptyFields(target, source) {
      if (!source || typeof source !== 'object') return;

      Object.keys(source).forEach((key) => {
        // Only set if target field is empty/falsy and source has a value
        if (!target[key] && source[key]) {
          target[key] = source[key];
        }
      });
    },
    async loadLoggedInUserData() {
      try {
        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_get_current_user_data',
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          const userData = data.data;

          // Merge billing data (only fills empty fields)
          this.mergeEmptyFields(this.customer, userData.billing);

          // Merge shipping data if available
          if (
            userData.shipping &&
            Object.values(userData.shipping).some((val) => val)
          ) {
            this.mergeEmptyFields(this.shippingAddress, userData.shipping);
          }
        }
      } catch (error) {
        // Silent fail - user can still fill in form manually
      }
    },
    async loadShippingMethods() {
      try {
        const checkoutItems = this.checkoutPayload?.items || [];

        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_get_shipping_methods',
            nonce: window.s7_nonces?.shipping || this.ajaxNonce,
            country: this.currentCountry,
            state: this.useDifferentShippingAddress
              ? this.shippingAddress.state
              : this.customer.state || '',
            postcode: this.useDifferentShippingAddress
              ? this.shippingAddress.postcode
              : this.customer.postcode || '',
            items: JSON.stringify(checkoutItems),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.shippingMethods = data.data;

          if (this.shippingMethods.length > 0) {
            this.autoSelectShippingMethod();

            if (
              !this.currentShippingMethod &&
              this.filteredShippingMethods.length > 0
            ) {
              this.currentShippingMethod = this.filteredShippingMethods[0];
              this.selectedShippingMethod = this.filteredShippingMethods[0];
            } else if (this.filteredShippingMethods.length === 0) {
              this.currentShippingMethod = null;
              this.selectedShippingMethod = null;
              this.shippingMethodNotification = `Geen verzendmethodes beschikbaar voor ${this.currentCountry === 'NL' ? 'Nederland' : this.currentCountry === 'BE' ? 'België' : this.currentCountry}`;
            }
          }
        } else {
          throw new Error(
            data.data?.message || 'Failed to load shipping methods',
          );
        }
      } catch (error) {
        this.submitErrors.push(
          'Kon verzendmethoden niet laden. Probeer de pagina te verversen.',
        );
      }
    },
    async loadPaymentMethods() {
      try {
        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_get_payment_methods',
            nonce: window.s7_nonces?.payment || this.ajaxNonce,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.paymentMethods = data.data;
          if (this.paymentMethods.length > 0) {
            this.currentPaymentMethod = this.paymentMethods[0];
          }
        } else {
          throw new Error(
            data.data?.message || 'Failed to load payment methods',
          );
        }
      } catch (error) {
        this.submitErrors.push(
          'Kon betaalmethoden niet laden. Probeer de pagina te verversen.',
        );
      }
    },
    calculatePricePerUnit(item) {
      try {
        if (item.pricePerUnitExVat && item.pricePerUnitExVat > 0) {
          return item.pricePerUnitExVat;
        }

        const baseItem = { ...item, quantity: 1, pricePerUnit: 0 };
        const baseVatBreakdown = calculateVatBreakdownByProduct([baseItem]);

        let printingCostPerUnit = 0;
        // Use per_package data if available, otherwise fallback to old calculation
        if (item.personalization?.printing?.per_package?.total_ex_vat) {
          printingCostPerUnit =
            item.personalization.printing.per_package.total_ex_vat;
        } else if (item.personalization?.printing?.total_ex_vat) {
          printingCostPerUnit =
            item.personalization.printing.total_ex_vat / (item.quantity || 1);
        }

        const result = roundCurrency(
          baseVatBreakdown.subtotalExVat + printingCostPerUnit,
        );

        return result;
      } catch (error) {
        return 0;
      }
    },
    loadCustomerData() {
      const storedCustomer = localStorage.getItem('customerData');
      if (storedCustomer) {
        const parsedData = JSON.parse(storedCustomer);
        this.customer = { ...this.customer, ...parsedData };

        // Load shipping address data if it exists
        if (parsedData.useDifferentShippingAddress !== undefined) {
          this.useDifferentShippingAddress =
            parsedData.useDifferentShippingAddress;
        }
        if (parsedData.shippingAddress) {
          this.shippingAddress = {
            ...this.shippingAddress,
            ...parsedData.shippingAddress,
          };
        }
      }

      this.loadLogoAttachments();
    },

    loadLogoAttachments() {
      const storedLogos = localStorage.getItem('s7_logo_attachments');
      if (storedLogos) {
        try {
          const logoData = JSON.parse(storedLogos);
          if (Array.isArray(logoData) && logoData.length > 0) {
            // Take the first logo attachment ID
            this.logoAttachmentId = logoData[0].id;
          }
        } catch (error) {
          this.showError('Kon logo bijlagen niet verwerken');
        }
      }

      // Listen for logo updates
      window.addEventListener('s7-logo-updated', (event) => {
        const attachments = event.detail.attachments;
        if (attachments && attachments.length > 0) {
          this.logoAttachmentId = attachments[0].id;
        } else {
          this.logoAttachmentId = '';
        }
      });
    },
    async loadAjaxNonce() {
      try {
        const response = await fetch(
          '/wp-admin/admin-ajax.php?action=get_s7_nonce',
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          this.ajaxNonce = data.data.order_nonce;
          window.s7_nonces = {
            order: data.data.order_nonce,
            shipping: data.data.shipping_nonce,
            payment: data.data.payment_nonce,
          };
        } else {
          throw new Error(data.data?.message || 'Failed to get nonces');
        }
      } catch (error) {
        this.submitErrors.push(
          'Kon beveiligingscontrole niet laden. Probeer de pagina te verversen.',
        );
      }
    },
    saveCustomerData() {
      const customerData = {
        ...this.customer,
        useDifferentShippingAddress: this.useDifferentShippingAddress,
        shippingAddress: this.shippingAddress,
      };
      localStorage.setItem('customerData', JSON.stringify(customerData));
    },
    editAddress() {},
    buildItemsArray() {
      return this.cartItems.map((item) => {
        return {
          id:
            item.id ||
            `package_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: item.packageName || 'Onbekend pakket',
          quantity: item.quantity || 1,
          pricePerUnitExVat: item.pricePerUnitExVat || 0,
          lineTotalExVat: item.lineTotalExVat || 0,
          s7_is_mailbox_package: item.s7_is_mailbox_package || false,
          products: (item.products || [])
            .filter((product) => {
              if (
                (item.packaging && item.packaging.id === product.id) ||
                (item.packagingProduct &&
                  item.packagingProduct.id === product.id)
              ) {
                return false;
              }
              return true;
            })
            .map((product) => ({
              id:
                product.id ||
                `product_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: product.name || 'Onbekend product',
              quantity: product.quantity || product.count || 1,
              price: parseFloat(product.price) || 0,
            })),
          foodDrinks: (item.foodDrinks || []).map((food) => ({
            id:
              food.id ||
              `food_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: food.name || 'Onbekende drank/voeding',
            quantity: food.quantity || food.count || 1,
            price: parseFloat(food.price) || 0,
          })),
          packaging: (() => {
            if (
              item.packaging &&
              typeof item.packaging === 'object' &&
              Object.keys(item.packaging).length > 0
            ) {
              return item.packaging;
            }

            if (
              !item.packaging ||
              (typeof item.packaging === 'object' &&
                Object.keys(item.packaging).length === 0)
            ) {
              return {
                id: 'default-packaging',
                name: 'Standaard verpakking',
                price: 0,
                taxClass: 'standard',
                taxRate: 21,
                taxLabel: 'BTW',
                count: 1,
                quantity: 1,
              };
            }

            return null;
          })(),
          personalization: item.personalization || {},
          basePackage: item.basePackage || null,
        };
      });
    },
    async submitOrder() {
      if (this.submittingOrderNow || this.isSubmitting) {
        return;
      }

      if (!this.isFormValid) {
        this.showFormErrors = true;
        return;
      }

      if (
        this.currentPaymentMethod &&
        this.cartItems.length > 0 &&
        !this.paymentInfoTriggered
      ) {
        this.pushAddPaymentInfoEvent();
        this.paymentInfoTriggered = true;
      }

      // Set flags immediately to prevent any race conditions
      this.submittingOrderNow = true;
      this.isSubmitting = true;
      this.submitErrors = [];

      // Disable form submission via Enter key
      const form = document.querySelector('form');
      if (form) {
        form.addEventListener(
          'submit',
          (e) => {
            if (this.submittingOrderNow || this.isSubmitting) {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }
          },
          { once: true },
        );
      }

      try {
        this.saveCustomerData();

        // If notice already shown, user clicking again means they accept it
        if (this.accountExistedMessage) {
          this.confirmedExistingAccount = true;
          this.accountExistedMessage = false;
        }

        if (
          (this.createAccount || this.isAccountRequired) &&
          !this.confirmedExistingAccount &&
          !this.isUserLoggedIn
        ) {
          const accountCheck = await this.checkIfAccountExists(
            this.customer.email,
          );

          if (accountCheck.exists) {
            this.accountExistedMessage = true;
            // Keep flags set to prevent double submission while showing message
            // Only reset when user explicitly proceeds
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
          }
        }

        if (!this.currentShippingMethod || !this.currentShippingMethod.cost) {
          throw new Error('Geen geldige verzendmethode geselecteerd');
        }

        const isMethodAvailable = this.filteredShippingMethods.some(
          (method) => method.id === this.currentShippingMethod.id,
        );

        if (!isMethodAvailable) {
          throw new Error(
            'De geselecteerde verzendmethode is niet meer beschikbaar voor dit land',
          );
        }

        const items = this.buildItemsArray();

        const payload = {
          customer: {
            billing: {
              first_name: this.customer.firstName || '',
              last_name: this.customer.lastName || '',
              email: this.customer.email || '',
              phone: this.customer.phone || '',
              company: this.customer.company || '',
              address_1:
                `${this.customer.street || ''} ${this.customer.houseNumber}${this.customer.houseAddition ? this.customer.houseAddition : ''}`.trim(),
              postcode: this.customer.postcode || '',
              city: this.customer.city || '',
              country: this.customer.country || 'NL',
            },
            shipping: this.useDifferentShippingAddress
              ? {
                  first_name: this.shippingAddress.firstName || '',
                  last_name: this.shippingAddress.lastName || '',
                  phone: this.shippingAddress.phone || '',
                  company: this.shippingAddress.company || '',
                  address_1:
                    `${this.shippingAddress.street || ''} ${this.shippingAddress.houseNumber}${this.shippingAddress.houseAddition ? this.shippingAddress.houseAddition : ''}`.trim(),
                  postcode: this.shippingAddress.postcode || '',
                  city: this.shippingAddress.city || '',
                  country: this.shippingAddress.country || 'NL',
                }
              : {},
            useDifferentShippingAddress: this.useDifferentShippingAddress,
          },
          account: {
            createAccount: this.createAccount || this.isAccountRequired,
            password: this.accountData.password || '',
            passwordConfirm: this.accountData.passwordConfirm || '',
            generatePassword: this.woocommerceSettings.generatePassword,
          },
          items: this.buildItemsArray(),
          shipping_method: this.currentShippingMethod || {},
          shipping_cost: this.currentShippingMethod.cost,
          payment_method: this.currentPaymentMethod || {},
          delivery_date: this.customer.deliveryDate || '',
          notes: this.customer.orderNotes || '',
          totals: {
            subtotalExVat: this.subtotalExVat || 0,
            volumeDiscountPercent: this.volumeDiscountPercent || 0,
            volumeDiscountAmount: this.volumeDiscountAmount || 0,
            highVat: this.vatBreakdown.highVat || 0,
            lowVat: this.vatBreakdown.lowVat || 0,
            shippingVat: this.vatBreakdown.shippingVat || 0,
            currency: 'EUR',
            vatBreakdown: this.vatBreakdown,
            vatRates: this.vatRatesArray,
          },
          meta: {
            source: 's7_checkout',
            currency: 'EUR',
            prices_include_tax: false,
          },
        };

        if (this.logoAttachmentId) {
          payload.logo_attachment_id = this.logoAttachmentId;
        }

        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_submit_order',
            nonce: this.ajaxNonce,
            payload: JSON.stringify(payload),
          }),
        });

        if (!response.ok) {
          let errorMessage = `HTTP error! status: ${response.status}`;
          let errorDetails = '';

          try {
            const errorData = await response.json();
            this.showError(
              errorData.message ||
                'Er is een fout opgetreden bij het plaatsen van de bestelling',
            );

            if (errorData.data && errorData.data.message) {
              errorMessage = errorData.data.message;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }

            if (errorData.data && errorData.data.errors) {
              errorDetails = JSON.stringify(errorData.data.errors);
              this.showError('Validatiefouten gevonden');
            }
          } catch (e) {
            this.showError('Kon foutmelding niet verwerken');
          }

          const fullErrorMessage = errorDetails
            ? `${errorMessage} - Details: ${errorDetails}`
            : errorMessage;
          throw new Error(fullErrorMessage);
        }

        const result = await response.json();

        if (result.success) {
          sessionStorage.setItem('s7_last_order_id', result.data.order_id);
          sessionStorage.setItem('s7_last_order_key', result.data.order_key);

          if (result.data.redirect_url) {
            // Clear cart immediately to prevent resubmission
            window.localStorage.removeItem('composedPackages');
            window.localStorage.removeItem('customerData');

            // Redirect immediately - don't wait for cleanup
            // Use replace to prevent back button from allowing resubmission
            window.location.replace(result.data.redirect_url);
            return; // Exit early, don't reset flags
          } else {
            this.submitErrors.push(
              'Bestelling succesvol geplaatst, maar geen redirect URL ontvangen.',
            );
            // Only reset flags if redirect failed
            this.isSubmitting = false;
            this.submittingOrderNow = false;
          }
        } else {
          const errorMessage = result.data?.message || 'Order creation failed';
          this.showError(errorMessage);
          this.submitErrors.push(errorMessage);
          // Reset flags on error so user can retry
          this.isSubmitting = false;
          this.submittingOrderNow = false;
        }
      } catch (error) {
        this.submitErrors.push(
          error.message ||
            'Er is iets misgegaan bij het verwerken van je bestelling.',
        );
        // Reset flags on error so user can retry
        this.isSubmitting = false;
        this.submittingOrderNow = false;
      }
    },

    handleExistingAccountProceed() {
      // User confirmed they want to proceed with existing account
      this.confirmedExistingAccount = true;
      this.accountExistedMessage = false;
      this.submitErrors = [];

      // Keep submission flags set - submitOrder will handle them
      // This prevents double submission if user clicks multiple times
      this.submitOrder();
    },

    async checkIfAccountExists(email) {
      try {
        const response = await fetch('/wp-admin/admin-ajax.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            action: 's7_check_account_exists',
            email: email,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          return data.data; // { exists: boolean, user_id: number|null }
        } else {
          throw new Error(data.data?.message || 'Failed to check account');
        }
      } catch (error) {
        return { exists: false, user_id: null };
      }
    },

    async updateVolumeDiscount() {
      if (this.cartItems.length === 0) return;

      const totalQuantity = this.cartItems.reduce(
        (sum, item) => sum + (item.quantity || 1),
        0,
      );

      this.storedVolumeDiscountPercent =
        this.getHardcodedVolumeDiscount(totalQuantity);

      try {
        const volumeDiscountPercent =
          await getVolumeDiscountPercent(totalQuantity);
        if (volumeDiscountPercent !== this.storedVolumeDiscountPercent) {
          this.storedVolumeDiscountPercent = volumeDiscountPercent;
        }
      } catch (error) {
        // Silent fail - uses hardcoded value
      }
    },

    async updateVatBreakdown() {
      if (this.cartItemsWithPrices.length === 0) {
        this.vatBreakdownData = {
          subtotalExVat: 0,
          subtotalAfterDiscount: 0,
          volumeDiscountPercent: 0,
          volumeDiscountAmount: 0,
          vatRates: new Map(),
          total: 0,
          totalInclVat: 0,
          totalVat: 0,
        };
        return;
      }

      const totalQuantity = this.cartItems.reduce(
        (sum, item) => sum + (item.quantity || 1),
        0,
      );
      const volumeDiscountPercent =
        this.storedVolumeDiscountPercent ||
        this.getHardcodedVolumeDiscount(totalQuantity);

      const shippingCost = this.currentShippingMethod?.cost || 0;

      try {
        const { prepareItemsWithCardPrices } = await import(
          './configurator/utils/priceUtils.js'
        );

        const preparedItems = await prepareItemsWithCardPrices(
          this.cartItemsWithPrices,
        );

        preparedItems.forEach((preparedItem, index) => {
          if (
            preparedItem.personalization?.selectedCard?.originalPrice &&
            this.cartItems[index]?.personalization?.selectedCard
          ) {
            this.cartItems[index].personalization.selectedCard = {
              ...this.cartItems[index].personalization.selectedCard,
              ...preparedItem.personalization.selectedCard,
            };
          }
        });

        const result = calculateVatBreakdownByProduct(
          preparedItems,
          volumeDiscountPercent,
          shippingCost,
        );
        this.vatBreakdownData = result;
      } catch {
        const result = calculateVatBreakdownByProduct(
          this.cartItemsWithPrices,
          volumeDiscountPercent,
          shippingCost,
        );
        this.vatBreakdownData = result;
      }
    },

    getShippingMethodMapping() {
      return {
        NL: {
          brievenbuspakket: {
            methodId: 'flat_rate',
            instanceId: '2',
            labelKeywords: ['brievenbuspost', 'brievenbus'],
            expectedCost: 4.25,
          },
          brievenbusdoosje: {
            methodId: 'flat_rate',
            instanceId: '5',
            labelKeywords: ['pakket', 'brievenbusdoosje'],
            expectedCost: 8.5,
          },
          groot_pakket: {
            methodId: 'flat_rate',
            instanceId: '3',
            labelKeywords: ['themapakket'],
            expectedCost: 8.5,
          },
        },
        BE: {
          brievenbuspakket: {
            methodId: 'flat_rate',
            instanceId: '5',
            labelKeywords: ['pakket', 'brievenbusdoosje'],
            expectedCost: 12.9,
          },
          brievenbusdoosje: {
            methodId: 'flat_rate',
            instanceId: '5',
            labelKeywords: ['pakket', 'brievenbusdoosje'],
            expectedCost: 12.9,
          },
          groot_pakket: {
            methodId: 'flat_rate',
            instanceId: '5',
            labelKeywords: ['pakket', 'brievenbusdoosje'],
            expectedCost: 12.9,
          },
        },
      };
    },

    findShippingMethodByMapping(packageType, country) {
      const mapping = this.getShippingMethodMapping();
      const countryMapping = mapping[country];

      if (!countryMapping || !packageType) {
        return null;
      }

      const methodConfig = countryMapping[packageType];
      if (!methodConfig) {
        return null;
      }

      const availableMethods = this.filteredShippingMethods;

      const method = availableMethods.find((m) => {
        const methodParts = m.id.split(':');
        const methodId = methodParts[0];
        const instanceId = methodParts[1];

        return (
          methodId === methodConfig.methodId &&
          instanceId === methodConfig.instanceId
        );
      });

      if (!method) {
        return availableMethods.find((m) => {
          const label = (m.label || '').toLowerCase();
          return methodConfig.labelKeywords.some((keyword) =>
            label.includes(keyword.toLowerCase()),
          );
        });
      }

      return method;
    },

    isThemapakketMethod(method) {
      if (!method) return false;

      const label = (method.label || '').toLowerCase();
      return label.includes('themapakket');
    },

    shouldAutoSelectShippingMethod() {
      if (!this.currentShippingMethod) {
        return true;
      }

      const isCurrentMethodAvailable = this.filteredShippingMethods.some(
        (method) => method.id === this.currentShippingMethod.id,
      );

      if (!isCurrentMethodAvailable) {
        return true;
      }

      if (
        this.packageType === 'brievenbusdoosje' &&
        this.isThemapakketMethod(this.currentShippingMethod)
      ) {
        return true;
      }

      if (this.lastPackageType && this.lastPackageType !== this.packageType) {
        return true;
      }

      if (this.lastCountry && this.lastCountry !== this.currentCountry) {
        return true;
      }

      return false;
    },

    autoSelectShippingMethod() {
      if (!this.shouldAutoSelectShippingMethod()) {
        return;
      }

      const packageType = this.packageType;
      const country = this.currentCountry;

      if (
        !packageType ||
        !country ||
        this.filteredShippingMethods.length === 0
      ) {
        return;
      }

      if (packageType === 'brievenbusdoosje') {
        const themapakketMethod = this.filteredShippingMethods.find((m) =>
          this.isThemapakketMethod(m),
        );

        if (
          themapakketMethod &&
          this.currentShippingMethod === themapakketMethod
        ) {
          const nonThemapakketMethod = this.filteredShippingMethods.find(
            (m) => !this.isThemapakketMethod(m),
          );

          if (nonThemapakketMethod) {
            this.currentShippingMethod = nonThemapakketMethod;
            this.selectedShippingMethod = nonThemapakketMethod;
            this.shippingMethodNotification = '';
            this.lastPackageType = packageType;
            this.lastCountry = country;
            return;
          }
        }
      }

      const targetMethod = this.findShippingMethodByMapping(
        packageType,
        country,
      );

      if (targetMethod) {
        this.currentShippingMethod = targetMethod;
        this.selectedShippingMethod = targetMethod;
        this.shippingMethodNotification = '';
      } else {
        this.currentShippingMethod = null;
        this.selectedShippingMethod = null;
        this.shippingMethodNotification =
          'Kies een verzendmethode voor dit pakkettype';
      }

      this.lastPackageType = packageType;
      this.lastCountry = country;
    },
    showError(message) {
      this.errors.general = message;
      setTimeout(() => {
        this.errors.general = '';
      }, 5000);
    },
    buildEcommerceItems() {
      const items = [];
      this.cartItems.forEach((item) => {
        const basePackageId = item.basePackage?.id || item.id || 0;
        const basePackageName =
          item.basePackage?.name || item.packageName || 'Onbekend pakket';
        const packageQuantity = item.quantity || 1;

        const filteredProducts = (item.products || []).filter((product) => {
          if (
            (item.packaging && item.packaging.id === product.id) ||
            (item.packagingProduct && item.packagingProduct.id === product.id)
          ) {
            return false;
          }
          return true;
        });

        const allProducts = [
          ...filteredProducts,
          ...(item.foodDrinks || []),
          ...(item.packaging ? [item.packaging] : []),
        ].filter(Boolean);

        allProducts.forEach((product) => {
          if (!product.id || !product.name) {
            return;
          }

          const productQuantityPer = product.quantity || product.count || 1;
          const productQuantity = productQuantityPer * packageQuantity;
          const productPrice = parseFloat(product.price || 0);

          items.push({
            item_id: product.id,
            item_name: product.name,
            quantity: productQuantity,
            price: productPrice,
            item_list_id: basePackageId,
            item_list_name: basePackageName,
            quantity_item_list: packageQuantity,
            quantity_per: productQuantityPer,
          });
        });

        let printingCostPerPackage = 0;
        if (item.personalization?.printing?.per_package?.total_ex_vat) {
          printingCostPerPackage =
            item.personalization.printing.per_package.total_ex_vat;
        } else if (item.personalization?.printing?.total_ex_vat) {
          printingCostPerPackage =
            item.personalization.printing.total_ex_vat / packageQuantity;
        }

        const totalPrintingCost = printingCostPerPackage * packageQuantity;

        if (totalPrintingCost > 0) {
          items.push({
            item_id: 'printing',
            item_name: 'Bedrukking',
            quantity: packageQuantity,
            price: printingCostPerPackage,
            item_list_id: basePackageId,
            item_list_name: basePackageName,
            quantity_item_list: packageQuantity,
            quantity_per: 1,
          });
        }
      });
      return items;
    },
    calculateTotalValue(items) {
      let total = items.reduce((sum, item) => {
        return sum + parseFloat(item.price || 0) * (item.quantity || 1);
      }, 0);

      this.cartItems.forEach((cartItem) => {
        let printingCostPerPackage = 0;
        if (cartItem.personalization?.printing?.per_package?.total_ex_vat) {
          printingCostPerPackage =
            cartItem.personalization.printing.per_package.total_ex_vat;
        } else if (cartItem.personalization?.printing?.total_ex_vat) {
          printingCostPerPackage =
            cartItem.personalization.printing.total_ex_vat /
            (cartItem.quantity || 1);
        }

        const totalPrintingCost =
          printingCostPerPackage * (cartItem.quantity || 1);
        total += totalPrintingCost;
      });

      return total;
    },
    pushBeginCheckoutEvent() {
      if (typeof window === 'undefined' || this.cartItems.length === 0) {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });

      const items = this.buildEcommerceItems();
      const value = this.calculateTotalValue(items);

      window.dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: 'EUR',
          value: value,
          items: items,
        },
      });
    },
    isAddressComplete() {
      if (this.useDifferentShippingAddress) {
        return !!(
          this.shippingAddress.street &&
          this.shippingAddress.houseNumber &&
          this.shippingAddress.postcode &&
          this.shippingAddress.city
        );
      }
      return !!(
        this.customer.street &&
        this.customer.houseNumber &&
        this.customer.postcode &&
        this.customer.city
      );
    },
    checkAndTriggerShippingInfo() {
      if (
        this.currentShippingMethod &&
        this.cartItems.length > 0 &&
        this.isAddressComplete() &&
        !this.shippingInfoTriggered
      ) {
        this.$nextTick(() => {
          if (!this.shippingInfoTriggered) {
            this.pushAddShippingInfoEvent();
            this.shippingInfoTriggered = true;
          }
        });
      }
    },
    pushAddShippingInfoEvent() {
      if (
        typeof window === 'undefined' ||
        !this.currentShippingMethod ||
        this.cartItems.length === 0 ||
        !this.isAddressComplete()
      ) {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });

      const items = this.buildEcommerceItems();
      const value = this.calculateTotalValue(items);
      const shippingCost = parseFloat(this.currentShippingMethod.cost || 0);

      window.dataLayer.push({
        event: 'add_shipping_info',
        ecommerce: {
          currency: 'EUR',
          value: value,
          shipping_tier:
            this.currentShippingMethod.label ||
            this.currentShippingMethod.method_id ||
            '',
          items: items,
        },
      });
    },
    pushAddPaymentInfoEvent() {
      if (
        typeof window === 'undefined' ||
        !this.currentPaymentMethod ||
        this.cartItems.length === 0
      ) {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });

      const items = this.buildEcommerceItems();
      const value = this.calculateTotalValue(items);
      const shippingCost = this.currentShippingMethod
        ? parseFloat(this.currentShippingMethod.cost || 0)
        : 0;

      window.dataLayer.push({
        event: 'add_payment_info',
        ecommerce: {
          currency: 'EUR',
          value: value + shippingCost,
          payment_type:
            this.currentPaymentMethod.title ||
            this.currentPaymentMethod.id ||
            '',
          items: items,
        },
      });
    },
  },
};
</script>
